/**
 * ENG-9477 — did the broker's tools actually register in this session?
 *
 * ## The gap this closes
 *
 * `bind-applicability.ts` decides whether "is it in `.mcp.json`?" is a question
 * worth asking of an integration, and for a BUILTIN the honest answer is no:
 * agt-live, elevenlabs, image-gen, video-gen, social-scraping, x-search and
 * ninjafy-notes are served through the platform's own MCP server and never get
 * a `.mcp.json` entry of their own. The bind probe therefore writes
 * `not_applicable` and moves on. Every word of that is correct.
 *
 * The defect is that nothing asks a DIFFERENT question in its place.
 * `not_applicable` reads as "nothing to check here" when it means "this one
 * needs a different check", and that check did not exist — so for at least
 * seven integrations there was no tool-level monitoring of any kind. CS-1613 is
 * what that costs: `agt_live_publish` absent from an agent's session for six
 * hours, a customer page frozen eighteen hours and thirteen versions behind,
 * the row reading `status: active` with `consecutive_connectivity_failures: 0`
 * throughout, and a human noticing before any monitor did.
 *
 * ## The right question, and who can answer it
 *
 * For a broker-delivered integration the question is "did its tools register in
 * the session", and the only process that knows is the one that did the
 * registering: the `augmented` MCP server. It holds BOTH sides — the expected
 * set comes back from `/host/mcp/tools/list`, and the registered set is what it
 * just built. Nothing else on the host has both numbers.
 *
 * Before this, that knowledge died where it was born. A per-tool failure was
 * caught and written to `console.error`, which Claude Code captures into its own
 * MCP log directory — a path that is not on the allowlist for `debug_query_logs`
 * or `debug_tail_logs`, and which `pane` (a raw tmux screen capture of the
 * parent) can never contain. One missed registration meant that tool was gone
 * for the life of the session, in the main loop and in every sub-agent, with the
 * only evidence in a log no read tool can reach.
 *
 * ## Why a file, and not an API call
 *
 * The report rides the manager's existing heartbeat into `agents.diagnostics`,
 * the same route `claudeMd` (ENG-8163) and `pidPressure` (ENG-8876) take, for
 * the same stated reason: only the host can measure it. The MCP server writes
 * the fact where the manager can pick it up, and the manager — which already
 * owns that write — carries it. Posting straight to the API from the MCP server
 * would have raced the heartbeat, which rebuilds `diagnostics` from its own
 * object on every beat and would drop any key it did not itself send.
 *
 * ## The NO SIGNAL contract, which consumers must honour
 *
 * `null` means we do not know: no file, unreadable, unparseable, or an MCP
 * server predating this change. It is NOT "zero failures" and must never be
 * coerced into a passing value — writing a 0 there would paint every agent
 * permanently and invisibly green, which is the precise failure this module
 * exists to end. Same contract as `claudeMd` and `pidPressure`.
 */

import { readFileSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';

/** Filename under the agent's home dir. Sibling of `project/`, not inside it. */
export const FORWARDED_TOOLS_REPORT_FILENAME = 'forwarded-tools.json';

/**
 * What one MCP-server startup registered, against what it was told to expect.
 *
 * `expected` is the size of the set `/host/mcp/tools/list` returned, so
 * `registered + skipped` should equal it exactly. `skipped` is not a failure:
 * a forwarded tool whose name collides with a locally-registered one is
 * deliberately not double-registered.
 */
export interface ForwardedToolsReport {
  /** Tools the API said this agent should have. */
  expected: number;
  /** Tools this process successfully registered. */
  registered: number;
  /** Tools deliberately not forwarded because a local tool owns the name. */
  skipped: number;
  /**
   * Names that threw during registration.
   *
   * A LIST rather than a count, on purpose: "2 failed" sends someone reading
   * logs; "agt_live_publish, agt_live_editing failed" names the customer
   * symptom. CS-1613 was exactly three named tools and no count would have
   * shortened it.
   */
  failed: string[];
  /** ISO timestamp of the registration, so a stale file is detectable. */
  at: string;
}

/** Absolute path of the report for an agent home dir. */
export function forwardedToolsReportPath(agentDir: string): string {
  return join(agentDir, FORWARDED_TOOLS_REPORT_FILENAME);
}

/**
 * Persist the report. Best-effort by design: this runs on the MCP server's
 * startup path, and a session that cannot write its own telemetry must still
 * come up. Returns whether the write landed so a caller can log the miss rather
 * than assume it worked.
 */
export function writeForwardedToolsReport(
  agentDir: string,
  report: ForwardedToolsReport,
): boolean {
  try {
    writeFileSync(forwardedToolsReportPath(agentDir), JSON.stringify(report), 'utf-8');
    return true;
  } catch {
    return false;
  }
}

/**
 * Read the report, or `null` for NO SIGNAL.
 *
 * Every failure mode collapses to `null` — absent, unreadable, unparseable, or
 * present but not shaped like a report. That last case matters: a truncated or
 * half-written file must not be read as a healthy zero, so the shape is checked
 * field by field rather than trusted from `JSON.parse` succeeding.
 */
export function readForwardedToolsReport(agentDir: string): ForwardedToolsReport | null {
  let raw: string;
  try {
    raw = readFileSync(forwardedToolsReportPath(agentDir), 'utf-8');
  } catch {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const r = parsed as Record<string, unknown>;
  const nums = ['expected', 'registered', 'skipped'] as const;
  for (const k of nums) {
    if (typeof r[k] !== 'number' || !Number.isFinite(r[k] as number)) return null;
  }
  if (!Array.isArray(r['failed']) || (r['failed'] as unknown[]).some((n) => typeof n !== 'string')) {
    return null;
  }
  if (typeof r['at'] !== 'string' || !r['at']) return null;
  return {
    expected: r['expected'] as number,
    registered: r['registered'] as number,
    skipped: r['skipped'] as number,
    failed: r['failed'] as string[],
    at: r['at'] as string,
  };
}

/**
 * Is this session's forwarded-tool surface complete?
 *
 * Two independent ways to be wrong, and both matter:
 *
 *   - a tool THREW during registration (`failed` non-empty), which is the
 *     CS-1613 shape; and
 *   - the numbers do not reconcile — `registered + skipped < expected` — which
 *     catches a tool that vanished between discovery and registration without
 *     throwing at all. Checking only `failed` would miss that entirely.
 *
 * `null` in, `null` out: no signal is not health, and a caller must branch on
 * the three states rather than treating "not unhealthy" as "healthy".
 */
export function isForwardedToolSurfaceComplete(
  report: ForwardedToolsReport | null,
): boolean | null {
  if (!report) return null;
  if (report.failed.length > 0) return false;
  return report.registered + report.skipped >= report.expected;
}
